import { useMemo } from 'react'
import { useStore } from '@tanstack/react-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import { ServicioCombobox } from '@/components/ui/servicio-combobox'
import { Plus, Trash2, User, Calendar, Percent } from 'lucide-react'
import { useServicios } from '@/hooks/useServicios'
import { useClientes } from '@/hooks/useClientes'
import type { Presupuesto } from '@/types/database'
import { cn, formatCurrency } from '@/lib/utils'
import { toast } from 'sonner'
import { usePresupuestoForm } from './forms/usePresupuestoForm'
import type { PresupuestoFormValues } from './forms/presupuestoSchema'
import { getFieldError } from '@/components/clientes/wizard/useClienteForm'
import { ALICUOTAS_IVA, CONDICIONES_PAGO, getIvaPorcentaje, detectarTipoPersona, type TipoPersona } from './presupuesto-utils'

// Re-export for pages
export type { PresupuestoFormValues } from './forms/presupuestoSchema'

interface PresupuestoFormProps {
  presupuesto?: Presupuesto | null
  onSubmit: (data: PresupuestoFormValues & { subtotal: number; total: number }) => void
  onCancel: () => void
  isLoading?: boolean
}

// Label para admin/representante segun tipo de persona
function getAdminLabel(tipo: TipoPersona): string {
  return tipo === 'sucesion' ? 'Administrador' : 'Representante Legal'
}

export function PresupuestoForm({ presupuesto, onSubmit, onCancel, isLoading }: PresupuestoFormProps) {
  const { data: servicios = [], isLoading: loadingServicios } = useServicios()
  const { data: clientes = [], isLoading: loadingClientes } = useClientes()

  const { form } = usePresupuestoForm({
    presupuesto,
    onSubmit: async (values) => {
      // Calculate totals from items
      const subtotal = values.items.reduce((acc, item) => {
        const itemBruto = item.cantidad * item.precio_unitario
        const itemNeto = itemBruto * (1 - item.descuento_porcentaje / 100)
        return acc + itemNeto
      }, 0)

      const descuento = subtotal * (values.descuento_porcentaje / 100)
      const baseImponible = subtotal - descuento
      const iva = baseImponible * (getIvaPorcentaje(values.iva_porcentaje) / 100)
      const total = baseImponible + iva

      // Call parent onSubmit with calculated totals
      onSubmit({
        ...values,
        subtotal,
        total,
      })
    },
  })

  // Reactive subscriptions to form store (re-renders on change)
  const items = useStore(form.store, (s) => s.values.items)
  const descuentoPorcentaje = useStore(form.store, (s) => s.values.descuento_porcentaje)
  const ivaPorcentaje = useStore(form.store, (s) => s.values.iva_porcentaje)
  const modoCliente = useStore(form.store, (s) => s.values.modo_cliente)
  const tipoPersona = useStore(form.store, (s) => s.values.tipo_persona)
  const clienteId = useStore(form.store, (s) => s.values.cliente_id)

  // Calculate totals (reactive to items, descuento, iva changes)
  const totales = useMemo(() => {
    const subtotal = items.reduce((acc: number, item) => {
      const itemBruto = (item?.cantidad ?? 0) * (item?.precio_unitario ?? 0)
      const itemDescuento = item?.descuento_porcentaje ?? 0
      const itemNeto = itemBruto * (1 - itemDescuento / 100)
      return acc + itemNeto
    }, 0)

    const descuento = subtotal * ((descuentoPorcentaje ?? 0) / 100)
    const baseImponible = subtotal - descuento
    const iva = baseImponible * (getIvaPorcentaje(ivaPorcentaje) / 100)
    const total = baseImponible + iva

    return { subtotal, descuento, iva, total }
  }, [items, descuentoPorcentaje, ivaPorcentaje])

  // Auto-fill client data when selecting from combobox
  const handleClienteSelect = (id: string) => {
    const cliente = clientes.find((c) => c.id === id)
    if (cliente) {
      form.setFieldValue('cliente_id', id)
      form.setFieldValue('cliente_nombre', cliente.nombre ?? '')
      form.setFieldValue('cliente_cuit', cliente.cuit?.toString() ?? null)
      form.setFieldValue('cliente_email', cliente.email ?? null)
      form.setFieldValue('cliente_condicion_iva', cliente.condicion_iva ?? null)
      form.setFieldValue('tipo_persona', detectarTipoPersona(cliente.cuit?.toString() ?? null))
      form.setFieldValue('admin_nombre', cliente.admin_nombre ?? null)
      form.setFieldValue('admin_cuit', cliente.admin_cuit ?? null)
    }
  }

  // Handle item field changes (recalculate subtotal)
  const handleItemChange = (index: number, field: 'cantidad' | 'precio_unitario' | 'descuento_porcentaje', value: number) => {
    const item = items[index]
    if (!item) return
    const cantidad = field === 'cantidad' ? value : (item.cantidad ?? 0)
    const precio = field === 'precio_unitario' ? value : (item.precio_unitario ?? 0)
    const descuento = field === 'descuento_porcentaje' ? value : (item.descuento_porcentaje ?? 0)
    const subtotalBruto = cantidad * precio
    const subtotalNeto = subtotalBruto * (1 - descuento / 100)
    form.setFieldValue(`items[${index}].subtotal`, subtotalNeto)
  }

  // Handle servicio selection (auto-fill precio + descripcion)
  const handleServicioSelect = (index: number, servicioId: string) => {
    const servicio = servicios.find((s) => s.id === servicioId)
    if (servicio) {
      form.setFieldValue(`items[${index}].servicio_id`, servicioId)
      form.setFieldValue(`items[${index}].descripcion`, servicio.descripcion ?? null)
      if (servicio.precio_estimado) {
        form.setFieldValue(`items[${index}].precio_unitario`, servicio.precio_estimado)
        handleItemChange(index, 'precio_unitario', servicio.precio_estimado)
      }
    }
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault()
        await form.handleSubmit()
        // Show toast if validation failed (form has field errors after submit)
        const fieldErrors = Object.values(form.state.fieldMeta).some(
          (meta) => meta?.errors && meta.errors.length > 0
        )
        if (fieldErrors) {
          toast.error('Completá los campos requeridos para crear el presupuesto')
        }
      }}
      className="space-y-8"
    >
      {/* Sección Cliente */}
      <section>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Cliente</h3>

        {/* Toggle: Existente / Nuevo */}
        <form.Field name="modo_cliente">
          {(field) => (
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                variant={field.state.value === 'existente' ? 'default' : 'outline'}
                size="sm"
                onClick={() => field.handleChange('existente')}
                className="flex-1"
              >
                Cliente Existente
              </Button>
              <Button
                type="button"
                variant={field.state.value === 'nuevo' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  field.handleChange('nuevo')
                  form.setFieldValue('cliente_id', null)
                }}
                className="flex-1"
              >
                Cliente Nuevo
              </Button>
            </div>
          )}
        </form.Field>

        {/* Modo Existente: Combobox */}
        {modoCliente === 'existente' && (
          <form.Field name="cliente_id">
            {(field) => (
              <div className="space-y-2">
                <Label>Seleccionar Cliente</Label>
                <ClienteCombobox
                  clientes={clientes}
                  value={field.state.value ?? ''}
                  onValueChange={handleClienteSelect}
                  placeholder="Buscar cliente por nombre o CUIT..."
                  disabled={loadingClientes}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                )}
              </div>
            )}
          </form.Field>
        )}

        {/* Modo Nuevo: Formulario completo */}
        {modoCliente === 'nuevo' && (
          <div className="rounded-xl border bg-card p-5 space-y-4">
            {/* Tipo Persona: Radio buttons inline */}
            <form.Field name="tipo_persona">
              {(field) => (
                <div className="space-y-2">
                  <Label>Tipo de Persona</Label>
                  <div className="flex gap-4">
                    {[
                      { value: 'fisica' as const, label: 'Persona Física' },
                      { value: 'juridica' as const, label: 'Persona Jurídica' },
                      { value: 'sucesion' as const, label: 'Sucesión Indivisa' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={cn(
                          'flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 transition-colors',
                          field.state.value === option.value
                            ? 'border-primary bg-primary/5 font-medium'
                            : 'border-muted hover:border-primary/50'
                        )}
                      >
                        <input
                          type="radio"
                          name="tipo_persona"
                          value={option.value}
                          checked={field.state.value === option.value}
                          onChange={(e) => field.handleChange(e.target.value as TipoPersona)}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </form.Field>

            {/* Nombre */}
            <form.Field name="cliente_nombre">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="cliente_nombre">
                    {tipoPersona === 'juridica' ? 'Razón Social' : 'Nombre Completo'} *
                  </Label>
                  <Input
                    id="cliente_nombre"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={tipoPersona === 'juridica' ? 'Empresa S.A.' : 'Juan Pérez'}
                    className="h-11"
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                  )}
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* CUIT */}
              <form.Field name="cliente_cuit">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="cliente_cuit">CUIT</Label>
                    <Input
                      id="cliente_cuit"
                      value={field.state.value ?? ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="30-12345678-9"
                      className="h-11"
                    />
                  </div>
                )}
              </form.Field>

              {/* Email */}
              <form.Field name="cliente_email">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="cliente_email">Email</Label>
                    <Input
                      id="cliente_email"
                      type="email"
                      value={field.state.value ?? ''}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="correo@ejemplo.com"
                      className="h-11"
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                    )}
                  </div>
                )}
              </form.Field>
            </div>

            {/* Condición IVA */}
            <form.Field name="cliente_condicion_iva">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="cliente_condicion_iva">Condición IVA</Label>
                  <Select value={field.state.value ?? ''} onValueChange={field.handleChange}>
                    <SelectTrigger id="cliente_condicion_iva" className="h-11">
                      <SelectValue placeholder="Seleccionar condición..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                      <SelectItem value="monotributista">Monotributista</SelectItem>
                      <SelectItem value="exento">Exento</SelectItem>
                      <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                      <SelectItem value="no_categorizado">No Categorizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            {/* Admin/Representante - solo para juridica y sucesion */}
            {(tipoPersona === 'juridica' || tipoPersona === 'sucesion') && (
              <div className="rounded-lg border border-dashed bg-muted/30 p-4 space-y-4">
                <p className="text-sm font-medium text-muted-foreground">
                  {getAdminLabel(tipoPersona)}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <form.Field name="admin_nombre">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="admin_nombre">Nombre</Label>
                        <Input
                          id="admin_nombre"
                          value={field.state.value ?? ''}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="Nombre del representante"
                          className="h-11"
                        />
                      </div>
                    )}
                  </form.Field>
                  <form.Field name="admin_cuit">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="admin_cuit">CUIT</Label>
                        <Input
                          id="admin_cuit"
                          value={field.state.value ?? ''}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="20-12345678-9"
                          className="h-11"
                        />
                      </div>
                    )}
                  </form.Field>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview de cliente seleccionado (modo existente) */}
        {modoCliente === 'existente' && clienteId && (
          <div className="mt-4 rounded-xl border bg-muted/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Cliente Seleccionado</span>
            </div>
            <div className="space-y-1">
              <form.Field name="cliente_nombre">
                {(field) => <p className="font-medium">{field.state.value}</p>}
              </form.Field>
              <form.Field name="cliente_cuit">
                {(field) => field.state.value && <p className="text-sm text-muted-foreground">CUIT: {field.state.value}</p>}
              </form.Field>
              <form.Field name="cliente_email">
                {(field) => field.state.value && <p className="text-sm text-muted-foreground">Email: {field.state.value}</p>}
              </form.Field>
            </div>
          </div>
        )}
      </section>

      {/* Sección Presupuesto */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Datos del Presupuesto
        </h3>
        <div className="rounded-xl border bg-card p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fecha */}
            <form.Field name="fecha">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="fecha">Fecha *</Label>
                  <Input
                    id="fecha"
                    type="date"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="h-11"
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Validez días */}
            <form.Field name="validez_dias">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="validez_dias">Validez (días)</Label>
                  <Input
                    id="validez_dias"
                    type="number"
                    min="1"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(parseInt(e.target.value) || 1)}
                    onBlur={field.handleBlur}
                    className="h-11"
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                  )}
                </div>
              )}
            </form.Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Descuento Global */}
            <form.Field name="descuento_porcentaje">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="descuento_porcentaje" className="flex items-center gap-1">
                    <Percent className="h-3 w-3" />
                    Descuento Global
                  </Label>
                  <Input
                    id="descuento_porcentaje"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                    onBlur={field.handleBlur}
                    className="h-11"
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                  )}
                </div>
              )}
            </form.Field>

            {/* IVA */}
            <form.Field name="iva_porcentaje">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="iva_porcentaje" className="flex items-center gap-1">
                    <Percent className="h-3 w-3" />
                    IVA
                  </Label>
                  <Select value={field.state.value} onValueChange={field.handleChange}>
                    <SelectTrigger id="iva_porcentaje" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALICUOTAS_IVA.map((alicuota) => (
                        <SelectItem key={alicuota.value} value={alicuota.value}>
                          {alicuota.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          {/* Condiciones de Pago */}
          <form.Field name="condiciones_pago">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="condiciones_pago">Condiciones de Pago</Label>
                <Select value={field.state.value ?? ''} onValueChange={field.handleChange}>
                  <SelectTrigger id="condiciones_pago" className="h-11">
                    <SelectValue placeholder="Seleccionar condiciones..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDICIONES_PAGO.map((condicion) => (
                      <SelectItem key={condicion.value} value={condicion.value}>
                        {condicion.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>
        </div>
      </section>

      {/* Sección Items */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Servicios a Presupuestar</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              form.pushFieldValue('items', {
                servicio_id: '',
                cantidad: 1,
                precio_unitario: 0,
                descuento_porcentaje: 0,
                subtotal: 0,
                descripcion: null,
              })
            }
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Servicio
          </Button>
        </div>

        <div className="rounded-xl border bg-card overflow-hidden">
          {/* Header */}
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b">
            <div className="col-span-5">Servicio</div>
            <div className="col-span-1 text-center">Cant.</div>
            <div className="col-span-2 text-right">Precio Unit.</div>
            <div className="col-span-1 text-center">Dto.%</div>
            <div className="col-span-2 text-right">Subtotal</div>
            <div className="col-span-1"></div>
          </div>

          {/* Items */}
          <div className="divide-y">
            {items.map((item, index) => {
              const bruto = (item?.cantidad ?? 0) * (item?.precio_unitario ?? 0)
              const dto = item?.descuento_porcentaje ?? 0
              const neto = bruto * (1 - dto / 100)

              return (
                <div key={index} className="p-4 hover:bg-muted/30 transition-colors">
                  <div className="grid grid-cols-12 gap-3 items-start">
                    {/* Servicio */}
                    <form.Field name={`items[${index}].servicio_id`}>
                      {(field) => (
                        <div className="col-span-12 md:col-span-5">
                          <Label className="md:hidden text-xs text-muted-foreground">Servicio</Label>
                          <ServicioCombobox
                            servicios={servicios}
                            value={field.state.value}
                            onValueChange={(value) => handleServicioSelect(index, value)}
                            placeholder={loadingServicios ? 'Cargando...' : 'Seleccionar servicio'}
                            disabled={loadingServicios}
                          />
                          {field.state.meta.errors.length > 0 && (
                            <p className="text-destructive text-sm mt-1">{getFieldError(field.state.meta.errors)}</p>
                          )}
                        </div>
                      )}
                    </form.Field>

                    {/* Cantidad */}
                    <form.Field name={`items[${index}].cantidad`}>
                      {(field) => (
                        <div className="col-span-4 md:col-span-1 space-y-1">
                          <Label className="md:hidden text-xs text-muted-foreground">Cant.</Label>
                          <Input
                            type="number"
                            min="1"
                            value={field.state.value ?? ''}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1
                              field.handleChange(val)
                              handleItemChange(index, 'cantidad', val)
                            }}
                            className="h-10 text-center"
                          />
                        </div>
                      )}
                    </form.Field>

                    {/* Precio Unitario */}
                    <form.Field name={`items[${index}].precio_unitario`}>
                      {(field) => (
                        <div className="col-span-4 md:col-span-2 space-y-1">
                          <Label className="md:hidden text-xs text-muted-foreground">Precio</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={field.state.value ?? ''}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0
                              field.handleChange(val)
                              handleItemChange(index, 'precio_unitario', val)
                            }}
                            className="h-10 text-right"
                          />
                        </div>
                      )}
                    </form.Field>

                    {/* Descuento */}
                    <form.Field name={`items[${index}].descuento_porcentaje`}>
                      {(field) => (
                        <div className="col-span-2 md:col-span-1 space-y-1">
                          <Label className="md:hidden text-xs text-muted-foreground">Dto.%</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={field.state.value ?? ''}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0
                              field.handleChange(val)
                              handleItemChange(index, 'descuento_porcentaje', val)
                            }}
                            className="h-10 text-center"
                          />
                        </div>
                      )}
                    </form.Field>

                    {/* Subtotal */}
                    <div className="col-span-6 md:col-span-2 space-y-1">
                      <Label className="md:hidden text-xs text-muted-foreground">Subtotal</Label>
                      <div className="h-10 px-3 flex items-center justify-end rounded-md border bg-muted/50 text-sm font-semibold">
                        {formatCurrency(neto)}
                      </div>
                    </div>

                    {/* Eliminar */}
                    <div className="col-span-6 md:col-span-1 flex items-end md:items-center justify-end md:justify-center h-10">
                      {items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => form.removeFieldValue('items', index)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Descripción / Detalle del servicio */}
                  {item?.servicio_id && (
                    <form.Field name={`items[${index}].descripcion`}>
                      {(field) => (
                        <div className="mt-2">
                          <Textarea
                            value={field.state.value ?? ''}
                            onChange={(e) => field.handleChange(e.target.value || null)}
                            onBlur={field.handleBlur}
                            placeholder="Detalle del servicio (se muestra en la impresión)"
                            className="text-sm min-h-[60px] resize-y bg-muted/30"
                            rows={2}
                          />
                        </div>
                      )}
                    </form.Field>
                  )}
                </div>
              )
            })}
          </div>

          {/* Totales */}
          <div className="border-t bg-muted/30 p-4">
            <div className="flex flex-col items-end gap-2">
              <div className="flex justify-between w-full max-w-xs text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-medium">{formatCurrency(totales.subtotal)}</span>
              </div>
              {totales.descuento > 0 && (
                <div className="flex justify-between w-full max-w-xs text-sm text-green-600">
                  <span>
                    Descuento ({descuentoPorcentaje}%):
                  </span>
                  <span>-{formatCurrency(totales.descuento)}</span>
                </div>
              )}
              {totales.iva > 0 && (
                <div className="flex justify-between w-full max-w-xs text-sm">
                  <span className="text-muted-foreground">
                    IVA ({getIvaPorcentaje(ivaPorcentaje)}%):
                  </span>
                  <span className="font-medium">{formatCurrency(totales.iva)}</span>
                </div>
              )}
              <div className="flex justify-between w-full max-w-xs text-lg font-bold border-t pt-2 mt-1">
                <span>Total:</span>
                <span className="text-primary">{formatCurrency(totales.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notas */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Notas y Observaciones</h3>
        <form.Field name="notas">
          {(field) => (
            <Textarea
              id="notas"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder="Condiciones de pago, aclaraciones, etc."
              className="min-h-[100px] resize-none"
            />
          )}
        </form.Field>
      </section>

      {/* Botones */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} className="min-w-[120px]">
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading} className="min-w-[160px]">
          {isLoading ? 'Guardando...' : presupuesto ? 'Actualizar Presupuesto' : 'Crear Presupuesto'}
        </Button>
      </div>
    </form>
  )
}
